import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FaSeedling, FaLeaf, FaHeart, FaHandsHelping, FaBookReader, FaGraduationCap, FaHandHoldingHeart, FaIndustry, FaUsers, FaEye, FaBullseye } from 'react-icons/fa';
import './css/OurResponsibilities.css';
import bannerImage from '../assets/images/BB-1- 21 June.png';
import SEO from '../components/SEO';

const OurResponsibilities = () => {
  return (
    <>
      <SEO
        title="Our Responsibilities - Environmental & Social Impact | Nimble Group"
        description="Learn about Nimble Group's commitment to environmental responsibility, social impact, and sustainable business practices. Our comprehensive approach to corporate responsibility and community engagement."
        keywords="corporate responsibility, environmental impact, social responsibility, sustainable business, community engagement, environmental stewardship, corporate sustainability"
        canonical="/responsibilities"
      />
      <OurResponsibilitiesContent />
    </>
  );
};

const OurResponsibilitiesContent = () => {
  const { scrollYProgress } = useScroll();

  // Slideshow removed, using video instead
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { 
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const ecoManufacturingPractices = [
    {
      title: "Biodegradable Solutions",
      description: "Non-toxic ingredients that naturally decompose",
      icon: "🌱"
    },
    {
      title: "Energy Efficiency",
      description: "Low-emission processes optimized for minimal environmental impact",
      icon: "⚡"
    },
    {
      title: "Sustainable Supply Chain",
      description: "Eco-friendly packaging and responsible sourcing",
      icon: "♻️"
    }
  ];

  const socialImpactPrograms = [
    {
      title: "Educational Upliftment",
      description: "Distribution of self-help and spiritual development literature",
      icon: <FaBookReader />
    },
    {
      title: "Child Education Support",
      description: "Partnerships with NGOs for underprivileged children's education",
      icon: <FaGraduationCap />
    },
    {
      title: "Product Donations",
      description: "Free cleaning and maintenance products for social institutions",
      icon: <FaHandHoldingHeart />
    }
  ];

  const greenChemistryPrinciples = [
    "Utilization of renewable, bio-based raw materials",
    "Development of biodegradable, non-persistent, operator-safe products",
    "Replacement of harmful solvents with eco-friendly alternatives",
    "Process designs minimizing toxicity, waste, and energy consumption",
    "Customer education for greener industrial practices"
  ];

  const csrFocusAreas = [
    {
      title: "Environmental Awareness",
      description: "Supporting awareness initiatives within industrial ecosystems",
      icon: <FaLeaf />
    },
    {
      title: "Health & Safety",
      description: "Promoting employee health, safety, and community welfare",
      icon: <FaHeart />
    },
    {
      title: "Education",
      description: "Active support for child education programs via NGO partnerships",
      icon: <FaGraduationCap />
    },
    {
      title: "Green Manufacturing",
      description: "Promoting responsible practices across industries",
      icon: <FaIndustry />
    },
    {
      title: "Product Donations",
      description: "Essential products for NGOs and health welfare",
      icon: <FaHandsHelping />
    }
  ];

  const innovationDeliverables = [
    "Superior Environment, Health & Safety (EHS) performance",
    "Reduced environmental footprint",
    "Enhanced operational efficiency",
    "Compliance with international green standards"
  ];

  return (
    <div className="responsibilities-page">
      <div className="background-pattern"></div>
      
      {/* Hero Banner Image Section */}
      <motion.div 
        className="hero-banner-image"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        style={{ position: 'relative', width: '100%', height: '400px', overflow: 'hidden' }}
      >
        <img
          src={bannerImage}
          alt="Hero Banner"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1
          }}
        />
        {/* Overlay for better text readability */}
        <div className="hero-overlay" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2
        }}></div>

        {/* Hero Content */}
        <div className="hero-content" style={{ position: 'relative', zIndex: 3 }}>
          <div className="hero-text">
            <motion.div
              className="hero-title-container"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <div className="title-decoration">
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Quote Below Banner */}
      <motion.div 
        className="banner-quote"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div className="main-quote">
          "Businesses need to go beyond the interest of their companies to the communities they serve."
          <div className="quote-author">— Shri Ratan Tata</div>
        </div>
        
      </motion.div>

      {/* Core Philosophy Section */}
      <motion.section 
        className="responsibility-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container">
            <FaSeedling />
          </div>
          <h2>Our Core Philosophy</h2>
        </motion.div>
        
        <motion.div className="philosophy-content" variants={itemVariants}>
          <motion.p className="intro-text content-justified">
            At Nimble Group, True value is not in what one creates, but in what one silently returns.
            Beyond achievement lies offering, beyond success lies surrender. Giving is not an act it is the
            dissolving of self into the suffering of the world.
          </motion.p>

          <motion.p className="intro-text content-justified">
            This is not strategy, but soul-work. This is the path, Beyond Business.
          </motion.p>

          <motion.p className="intro-text content-justified">
            There comes a time in every journey where the hands that once built must begin to offer. Not
            for applause, not for recognition, but simply because the inner being knows, what is received
            must return. To live only for oneself, or even for the walls of an enterprise, is to remain in the
            shallow waters of existence. But to extend one's presence to the lives that lie beyond the map
            of transactions, that is the real awakening.
          </motion.p>

          <motion.p className="intro-text content-justified">
            True offering begins where calculation ends. It is not bound to roles, routines, or returns. It
            flows from a space of stillness, where the heart silently bows to the suffering it sees, and the
            soul recognizes its responsibility — not to act out of pity, but from shared humanity.
          </motion.p>

          <motion.p className="intro-text content-justified">
            To give where the hand of commerce does not reach... to nourish where profit finds no
            meaning... to heal without seeking gratitude… this is the fragrance of a life lived beyond
            business.
          </motion.p>

          <motion.p className="intro-text content-justified">
            Here, service is not an action but a presence. Giving is not a line item — it is a reflection of
            one's inner clarity. And fulfillment does not arise from expansion, but from dissolution of the
            self into the greater good.
          </motion.p>

          <motion.div 
            className="philosophy-reminder-enhanced"
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            <div className="reminder-icon">✨</div>
            <h3 className="reminder-title">
              Let this philosophy be a remembrance:
            </h3>
            <div className="reminder-text">
              <p className="reminder-line">That what you build may pass,</p>
              <p className="reminder-line eternal">But what you give without attachment becomes eternal.</p>
            </div>
            <div className="reminder-decoration"></div>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Social Impact Programs */}
      <motion.section 
        className="responsibility-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container">
            <FaHandsHelping />
          </div>
          <h2>Our Social Impact</h2>
        </motion.div>

        <motion.div className="philosophy-content" variants={itemVariants}>
          <motion.div className="section-subtitle">
            <h3 style={{ color: '#4CAF50', fontSize: '1.5rem', marginBottom: '1rem', fontStyle: 'italic' }}>
              Our Sacred Offerings — Expressions of Inner Responsibility
            </h3>
          </motion.div>

          <motion.p className="intro-text content-justified">
            In the stillness beyond ambition, when the self begins to dissolve, giving becomes not an
            activity but a sacred offering. These initiatives are not projects of pride, but acts of silent
            gratitude to the world that sustains us.
          </motion.p>

          <motion.p className="intro-text content-justified">
            Each gesture flows from the deeper understanding that life is not about accumulation, but
            about conscious return. What is given to us must pass through us, refined by care, expanded
            by intention, and shared without claim.
          </motion.p>
        </motion.div>

        <motion.div className="social-impact-grid">
          <motion.div 
            className="impact-card"
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
          >
            <div className="impact-icon">📚</div>
            <h3>Awakening Through Words — A Million Lights in Print</h3>
            <p>
              We have set forth on a timeless offering to distribute one lakh self-help books, free of cost, to
              individuals across the country. As of March 2025, 17,800 such books have quietly found their
              way into lives seeking meaning.
            </p>
            <p style={{ fontStyle: 'italic', marginTop: '1rem', color: '#4CAF50' }}>
              This is not a campaign — it is a prayer in print.
            </p>
            <p>
              We believe every soul carries immense potential, and a book, in the right hands at the right
              moment, can become the key to inner expansion. These books are not given as gifts, but as
              instruments of awakening — to guide one closer to their true self, their dharma, their deeper
              purpose.
            </p>
          </motion.div>

          <motion.div 
            className="impact-card"
            variants={itemVariants}
            whileHover={{ scale: 1.02 }}
          >
            <div className="impact-icon">🎓</div>
            <h3>Education of the Child, Evolution of the World</h3>
            <p>
              We hold the deep conviction that each child is a mirror of the future, and true giving begins
              when we nourish the most delicate lives.
            </p>
            <p>
              Through humble financial offerings, we support NGOs dedicated to child education — not as a
              gesture of charity, but as an act of preserving hope.
            </p>
            <p>
              Every educated child is a ripple in the collective consciousness, pushing humanity gently toward
              wisdom, harmony, and peace.
            </p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* CSR Focus Areas */}
      <motion.section 
        className="responsibility-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <motion.div className="section-header" variants={itemVariants}>
          <div className="icon-container">
            <FaHeart />
          </div>
          <h2>Our CSR Focus Areas</h2>
        </motion.div>

        <motion.div className="csr-focus-grid">
          {csrFocusAreas.map((area, index) => (
            <motion.div 
              key={index}
              className="csr-focus-card"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <div className="focus-icon">{area.icon}</div>
              <h3>{area.title}</h3>
              <p>{area.description}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.p 
          className="stakeholder-note"
          variants={itemVariants}
        >
          At Nimble, we view every stakeholder — from machines and materials to air, water, and
          human lives — as integral elements of our shared ecosystem.
        </motion.p>
      </motion.section>

      {/* Call to Action */}
      <motion.section 
        className="cta-section"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="cta-content">
          <h2>Let's Build a Better Tomorrow</h2>
          <p>
            Join us in our mission to create industrial solutions that respect both progress and nature.
            Together, we can make green the new standard.
          </p>
          <motion.button 
            className="cta-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Partner With Us
          </motion.button>
        </div>
      </motion.section>
    </div>
  );
};

export default OurResponsibilities; 